/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/refund
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyOrderKey,
  ShopifyReturnKey,
  ShopifyReverseDeliveryKey,
  ShopifyReverseDeliveryToReverseFulfillmentOrderKey,
  ShopifyReverseFulfillmentOrderKey,
  ShopifyReverseFulfillmentOrderLineItemKey,
  ShopifyReverseFulfillmentOrderLineItemToReverseFulfillmentOrderKey,
  ShopifyReverseFulfillmentOrderToOrderKey,
  ShopifyReverseFulfillmentOrderToReturnKey,
  ShopifyReverseFulfillmentOrderToReverseDeliveriesKey,
  ShopifyReverseFulfillmentOrderToReverseFulfillmentOrderLineItemsKey,
  ShopifyReverseFulfillmentOrderToShopKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyReverseFulfillmentOrderKey,
  "2024-07": {
    name: "Shopify Reverse Fulfillment Order",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Reverse Fulfillment Order Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyReverseFulfillmentOrderToReverseFulfillmentOrderLineItemsKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "lineItems" } },
        configuration: {
          relatedModelKey: ShopifyReverseFulfillmentOrderLineItemKey,
          inverseFieldKey: ShopifyReverseFulfillmentOrderLineItemToReverseFulfillmentOrderKey,
        },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReverseFulfillmentOrderToOrderKey,
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "order", graphQLSelection: "id" } },
      },
      {
        name: "Reverse Deliveries",
        fieldType: FieldType.HasMany,
        key: ShopifyReverseFulfillmentOrderToReverseDeliveriesKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "reverseDeliveries" } },
        configuration: {
          relatedModelKey: ShopifyReverseDeliveryKey,
          inverseFieldKey: ShopifyReverseDeliveryToReverseFulfillmentOrderKey,
        },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "status", isShopifyEnum: true } },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [{ name: "OPEN" }, { name: "CLOSED" }, { name: "CANCELED" }],
        },
      },
      {
        name: "Third Party Confirmation Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "thirdPartyConfirmation",
            graphQLSelection: `status`,
            isShopifyEnum: true,
          },
        },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [
            { name: "ACCEPTED" },
            { name: "CANCEL_ACCEPTED" },
            { name: "CANCEL_REJECTED" },
            { name: "PENDING_ACCEPTANCE" },
            { name: "PENDING_CANCELATION" },
            { name: "REJECTED" },
          ],
        },
      },
      {
        name: "Return",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReverseFulfillmentOrderToReturnKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "return_id", graphQLFieldKey: "returnId", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifyReturnKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReverseFulfillmentOrderToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "order.returns",
      resource: "return.reverseFulfillmentOrders",
      additionalFields: ["returnId:id"],
    },
  },
};

export const ShopifyReverseFulfillmentOrder = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
